import React, { memo, useEffect, useMemo, useRef, useState } from '../../../../lib/teact/teact';
import { getActions, getGlobal, withGlobal } from '../../../../global';

import type {
  ApiBaseCurrency, ApiCurrencyRates, ApiNetwork, ApiStakingState, ApiWalletWithVersionInfo,
} from '../../../../api/types';
import type {
  Account, AccountSettings, GlobalState, WalletCollection, WalletCollectionTabOrderId,
} from '../../../../global/types';
import type { TabWithProperties } from '../../../ui/TabList';
import { AccountSelectorState } from '../../../../global/types';
import { SettingsState } from '../../../../global/types';

import { IS_TELEGRAM_APP } from '../../../../config';
import {
  selectCurrentAccountId,
  selectEnclaveToken,
  selectHasPassword,
  selectIsEnclaveSessionValid,
  selectIsMnemonicAccount,
  selectNetworkAccounts,
  selectOrderedAccounts,
} from '../../../../global/selectors';
import buildClassName from '../../../../util/buildClassName';
import { captureEvents, SwipeDirection } from '../../../../util/captureEvents';
import { getChainsSupportingLedger } from '../../../../util/chain';
import { vibrate } from '../../../../util/haptics';
import { MEMO_EMPTY_ARRAY } from '../../../../util/memo';
import { disableSwipeToClose, enableSwipeToClose } from '../../../../util/modalSwipeManager';
import { suspendTelegramBackButton } from '../../../../util/telegram/backButtonManager';
import {
  getWalletCollectionTabOrderId,
  normalizeWalletCollectionTabOrder,
  SYSTEM_WALLET_COLLECTION_TAB_IDS,
} from '../../../../util/walletCollectionOrder';
import { IS_LEDGER_SUPPORTED, IS_TOUCH_ENV } from '../../../../util/windowEnvironment';
import { buildTabs, getCurrentTabIndex } from './helpers/tabsHelper';
import { AccountTab, DEFAULT_TAB, OPEN_CONTEXT_MENU_CLASS_NAME } from './constants';

import useEffectOnce from '../../../../hooks/useEffectOnce';
import useFlag from '../../../../hooks/useFlag';
import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';
import { useMultipleAccountsBalances } from '../../../../hooks/useMultipleAccountsBalances';
import useScrolledState from '../../../../hooks/useScrolledState';
import useSortableList from '../../../../hooks/useSortableList';
import useSyncEffect from '../../../../hooks/useSyncEffect';
import { useFilteredAccounts } from './hooks/useFilteredAccounts';

import AuthImportViewAccount from '../../../auth/AuthImportViewAccount';
import LedgerConnect from '../../../ledger/LedgerConnect';
import LedgerSelectWallets from '../../../ledger/LedgerSelectWallets';
import Modal from '../../../ui/Modal';
import TabList from '../../../ui/TabList';
import Transition from '../../../ui/Transition';
import LogOutModal from '../LogOutModal';
import AccountSelectorFooter from './AccountSelectorFooter';
import AccountSelectorHeader from './AccountSelectorHeader';
import AccountsGridView from './AccountsGridView';
import AccountsListView, { ACCOUNT_HEIGHT_PX } from './AccountsListView';
import AddAccountPasswordModal from './AddAccountPasswordModal';
import AddAccountSelector from './AddAccountSelector';
import {
  WalletCollectionEditor,
  type WalletCollectionManagerItem,
  WalletCollectionsManager,
} from './WalletCollections';

import modalStyles from '../../../ui/Modal.module.scss';
import styles from './AccountSelectorModal.module.scss';

const CREATE_COLLECTION_TAB_ID = 999;
const COLLECTION_TAB_ID_OFFSET = 1000;

type ReorderableSelectorTab = {
  orderId: WalletCollectionTabOrderId;
  tab: TabWithProperties;
  systemTab?: AccountTab;
  collection?: WalletCollection;
};

function getSystemTabOrderId(tab: AccountTab): WalletCollectionTabOrderId | undefined {
  switch (tab) {
    case AccountTab.My:
      return SYSTEM_WALLET_COLLECTION_TAB_IDS.my;
    case AccountTab.Ledger:
      return SYSTEM_WALLET_COLLECTION_TAB_IDS.ledger;
    case AccountTab.View:
      return SYSTEM_WALLET_COLLECTION_TAB_IDS.view;
    default:
      return undefined;
  }
}

function getSystemTabIcon(tab: AccountTab) {
  switch (tab) {
    case AccountTab.Ledger:
      return 'icon-ledger';
    case AccountTab.View:
      return 'icon-eye-filled';
    default:
      return 'icon-wallet';
  }
}

function countSystemTabAccounts(accounts: Array<[string, Account]>, tab: AccountTab) {
  return accounts.filter(([, account]) => {
    switch (tab) {
      case AccountTab.My:
        return account.type === 'mnemonic' || account.type === 'hardware';
      case AccountTab.Ledger:
        return account.type === 'hardware';
      case AccountTab.View:
        return account.type === 'view';
      default:
        return false;
    }
  }).length;
}

interface AccountSelectorOpenProps {
  isOpen: true;
  currentAccountId: string;
  baseCurrency: ApiBaseCurrency;
  currencyRates: ApiCurrencyRates;
  orderedAccounts: Array<[string, Account]>;
  networkAccounts?: Record<string, Account>;
  byAccountId: GlobalState['byAccountId'];
  tokenInfo: GlobalState['tokenInfo'];
  stakingDefault: ApiStakingState;
  settingsByAccountId: Record<string, AccountSettings>;
  walletCollections?: WalletCollection[];
  walletCollectionTabOrder?: Partial<Record<ApiNetwork, WalletCollectionTabOrderId[]>>;
  activeTab?: number;
  viewModeInitial?: 'cards' | 'list';
  areTokensWithNoCostHidden?: boolean;
  isSensitiveDataHidden?: true;
  isTestnet?: boolean;
  isLoading?: boolean;
  error?: string;
  hasPassword: boolean;
  accountWalletVersions?: ApiWalletWithVersionInfo[];
  canAddSubwallet: boolean;
  forceAddingTonOnlyAccount?: boolean;
  initialAuthState?: AccountSelectorState;
  shouldHideAddAccountBackButton?: boolean;
}

type StateProps = AccountSelectorOpenProps
  | ({ isOpen: false } & Partial<Omit<AccountSelectorOpenProps, 'isOpen'>>);

function AccountSelectorModal({
  isOpen,
  currentAccountId = '',
  baseCurrency,
  currencyRates,
  orderedAccounts = MEMO_EMPTY_ARRAY,
  networkAccounts,
  byAccountId,
  tokenInfo,
  stakingDefault,
  settingsByAccountId,
  walletCollections = MEMO_EMPTY_ARRAY,
  walletCollectionTabOrder,
  activeTab = DEFAULT_TAB,
  viewModeInitial,
  areTokensWithNoCostHidden,
  isSensitiveDataHidden,
  isTestnet,
  isLoading,
  error,
  hasPassword,
  accountWalletVersions,
  canAddSubwallet = false,
  forceAddingTonOnlyAccount,
  initialAuthState,
  shouldHideAddAccountBackButton,
}: StateProps) {
  const {
    closeAccountSelector,
    switchAccount,
    setAccountSelectorTab,
    setAccountSelectorViewMode,
    setPendingWalletCollection,
    rebuildOrderedAccountIds,
    updateOrderedAccountIds,
    addAccount,
    createSubWallet,
    clearAccountError,
    openSettingsWithState,
    resetHardwareWalletConnect,
    clearAccountLoading,
    openWalletRenameModal,
    saveWalletCollection,
    deleteWalletCollection,
    reorderWalletCollectionTabs,
  } = getActions();

  const lang = useLang();
  const contentRef = useRef<HTMLDivElement>();

  const initialRenderingKey = viewModeInitial === 'list'
    ? AccountSelectorState.List
    : AccountSelectorState.Cards;
  const [renderingKey, setRenderingKey] = useState<AccountSelectorState>(initialRenderingKey);
  const skipAnimationToKeyRef = useRef<AccountSelectorState | undefined>();
  const [isLogOutModalOpen, openLogOutModal, closeLogOutModal] = useFlag(false);
  const [logOutAccountId, setLogOutAccountId] = useState<string | undefined>();
  const [isNewAccountImporting, setIsNewAccountImporting] = useState<boolean>(false);
  const [isAddingSubwallet, setIsAddingSubwallet] = useState<boolean>(false);
  const [previousViewMode, setPreviousViewMode] = useState<AccountSelectorState>(initialRenderingKey);
  const [shouldReturnToStartScreen, setShouldReturnToStartScreen] = useState<boolean>(false);
  const [activeCollectionId, setActiveCollectionId] = useState<string | undefined>();
  const [editingCollectionId, setEditingCollectionId] = useState<string | undefined>();
  const isCollectionNavigationActive = renderingKey === AccountSelectorState.Collections
    || renderingKey === AccountSelectorState.CollectionEditor;

  useEffect(() => {
    if (!IS_TELEGRAM_APP || !isOpen || isCollectionNavigationActive) return undefined;
    return suspendTelegramBackButton();
  }, [isCollectionNavigationActive, isOpen]);

  const hasOtherWalletVersions = useMemo(() => (
    (accountWalletVersions?.filter((v) => v.lastTxId || v.version === 'W5').length ?? 0) > 1
  ), [accountWalletVersions]);

  const tabs = useMemo(() => buildTabs(isTestnet ?? false, lang), [isTestnet, lang]);
  const currentTabIndex = useMemo(() => getCurrentTabIndex(tabs, activeTab), [activeTab, tabs]);
  const selectedAccountTab = tabs[currentTabIndex]?.id ?? DEFAULT_TAB;
  const tabFilteredAccounts = useFilteredAccounts(orderedAccounts, selectedAccountTab);
  const network: ApiNetwork = isTestnet ? 'testnet' : 'mainnet';
  const networkCollections = useMemo(
    () => walletCollections.filter((collection) => collection.network === network),
    [walletCollections, network],
  );
  const availableAccountIds = useMemo(
    () => new Set(orderedAccounts.map(([accountId]) => accountId)),
    [orderedAccounts],
  );
  const activeCollectionIndex = networkCollections.findIndex(({ id }) => id === activeCollectionId);
  const activeCollection = networkCollections[activeCollectionIndex];
  const selectedTab = activeCollection ? AccountTab.All : selectedAccountTab;
  const reorderableSelectorTabs = useMemo<ReorderableSelectorTab[]>(() => [
    ...tabs.flatMap((tab) => {
      const orderId = getSystemTabOrderId(tab.id);
      return orderId ? [{ orderId, tab, systemTab: tab.id }] : [];
    }),
    ...networkCollections.map((collection, index) => ({
      orderId: getWalletCollectionTabOrderId(collection.id),
      tab: {
        id: COLLECTION_TAB_ID_OFFSET + index,
        title: collection.name,
        isTelemetryBlocked: true,
      },
      collection,
    })),
  ], [networkCollections, tabs]);
  const orderedTabIds = useMemo(() => normalizeWalletCollectionTabOrder(
    reorderableSelectorTabs.map(({ orderId }) => orderId),
    walletCollectionTabOrder?.[network],
  ), [network, reorderableSelectorTabs, walletCollectionTabOrder]);
  const orderedSelectorTabs = useMemo(() => {
    const tabsByOrderId = new Map(reorderableSelectorTabs.map((item) => [item.orderId, item]));
    return orderedTabIds.map((id) => tabsByOrderId.get(id)!);
  }, [orderedTabIds, reorderableSelectorTabs]);
  const selectorTabs = useMemo(() => [
    tabs[0],
    ...orderedSelectorTabs.map(({ tab }) => tab),
    {
      id: CREATE_COLLECTION_TAB_ID,
      icon: 'icon-plus',
      title: lang('New Collection'),
    },
  ], [lang, orderedSelectorTabs, tabs]);
  const activeSelectorTabId = activeCollection
    ? COLLECTION_TAB_ID_OFFSET + activeCollectionIndex
    : selectedAccountTab;
  const currentSelectorTabIndex = Math.max(
    0,
    selectorTabs.findIndex(({ id }) => id === activeSelectorTabId),
  );
  const collectionManagerItems = useMemo<WalletCollectionManagerItem[]>(() => orderedSelectorTabs.map((item) => {
    if (item.collection) {
      const walletsCount = item.collection.accountIds.filter((accountId) => availableAccountIds.has(accountId)).length;
      return {
        id: item.orderId,
        name: item.collection.name,
        walletsCount,
        icon: 'icon-tag',
        collectionId: item.collection.id,
      };
    }

    return {
      id: item.orderId,
      name: item.tab.title,
      walletsCount: countSystemTabAccounts(orderedAccounts, item.systemTab!),
      icon: getSystemTabIcon(item.systemTab!),
      isSystem: true,
    };
  }), [availableAccountIds, orderedAccounts, orderedSelectorTabs]);
  const filteredAccounts = useMemo(() => {
    if (!activeCollection) return tabFilteredAccounts;

    const collectionAccountIds = new Set(activeCollection.accountIds);
    return orderedAccounts.filter(([accountId]) => collectionAccountIds.has(accountId));
  }, [activeCollection, orderedAccounts, tabFilteredAccounts]);
  const { balancesByAccountId, totalBalance, addressLineChainsByAccountId } = useMultipleAccountsBalances({
    filteredAccounts,
    sourceAccounts: networkAccounts,
    byAccountId,
    tokenInfo,
    settingsByAccountId,
    areTokensWithNoCostHidden,
    baseCurrency,
    currencyRates,
    stakingDefault,
  });
  const orderedAccountIds = useMemo(
    () => filteredAccounts.map(([accountId]) => accountId),
    [filteredAccounts],
  );
  const handleAccountsOrderChange = useLastCallback((newOrder: string[]) => {
    updateOrderedAccountIds({ orderedAccountIds: newOrder });
  });
  const { sortState, handleDrag, handleDragEnd } = useSortableList(
    orderedAccountIds,
    ACCOUNT_HEIGHT_PX,
    handleAccountsOrderChange,
  );

  const {
    isScrolled,
    isAtEnd: noButtonsSeparator,
    update: handleScrollInitialize,
    handleScroll,
  } = useScrolledState();

  useEffectOnce(rebuildOrderedAccountIds);

  useEffect(() => {
    if (activeCollectionId && !networkCollections.some(({ id }) => id === activeCollectionId)) {
      setActiveCollectionId(undefined);
    }
  }, [activeCollectionId, networkCollections]);

  useEffect(() => {
    if (!isOpen) return undefined;

    disableSwipeToClose();

    return enableSwipeToClose;
  }, [isOpen]);

  useSyncEffect(() => {
    if (!isOpen || forceAddingTonOnlyAccount || initialAuthState === undefined) {
      skipAnimationToKeyRef.current = undefined;
      return;
    }

    const state = initialAuthState;
    let targetKey: AccountSelectorState;
    if (state === AccountSelectorState.AddAccountConnectHardware && IS_LEDGER_SUPPORTED && !isTestnet) {
      targetKey = AccountSelectorState.AddAccountConnectHardware;
    } else if (state === AccountSelectorState.AddAccountViewMode
      || state === AccountSelectorState.AddAccountInitial) {
      targetKey = state;
    } else {
      targetKey = initialRenderingKey;
    }

    skipAnimationToKeyRef.current = targetKey;
    setRenderingKey(targetKey);
  }, [isOpen, forceAddingTonOnlyAccount, initialAuthState, initialRenderingKey, isTestnet]);

  useEffect(() => {
    if (!isOpen) return;

    if (forceAddingTonOnlyAccount) {
      handleNewAccountClick();
      return;
    }

    if (initialAuthState === AccountSelectorState.AddAccountConnectHardware && IS_LEDGER_SUPPORTED && !isTestnet) {
      handleImportHardwareWalletClick();
    }
  }, [isOpen, forceAddingTonOnlyAccount, initialAuthState, isTestnet]);

  const handleCloseAccountSelectorForced = useLastCallback(() => {
    closeAccountSelector(undefined, { forceOnHeavyAnimation: true });
  });

  const handleModalClose = useLastCallback(() => {
    setRenderingKey(initialRenderingKey);
    setIsNewAccountImporting(false);
    setIsAddingSubwallet(false);
    setShouldReturnToStartScreen(false);
    setPreviousViewMode(initialRenderingKey);
    setActiveCollectionId(undefined);
    setEditingCollectionId(undefined);
    clearAccountLoading();
  });

  const handleBackFromAddAccount = useLastCallback(() => {
    switch (renderingKey) {
      case AccountSelectorState.AddAccountPassword:
        setRenderingKey(AccountSelectorState.AddAccountInitial);
        setIsAddingSubwallet(false);
        clearAccountError();
        break;

      case AccountSelectorState.AddAccountViewMode:
      case AccountSelectorState.AddAccountConnectHardware:
        if (shouldReturnToStartScreen) {
          setPendingWalletCollection({});
          setRenderingKey(previousViewMode);
          setShouldReturnToStartScreen(false);
        } else {
          setRenderingKey(AccountSelectorState.AddAccountInitial);
        }
        break;

      case AccountSelectorState.AddAccountSelectHardware:
        setRenderingKey(AccountSelectorState.AddAccountConnectHardware);
        break;

      default:
        setPendingWalletCollection({});
        setRenderingKey(previousViewMode);
    }
  });

  const handleSwitchAccount = useLastCallback((accountId: string) => {
    vibrate();
    handleCloseAccountSelectorForced();

    if (accountId !== currentAccountId) {
      switchAccount({ accountId });
    }
  });

  const handleNewAccountClick = useLastCallback(() => {
    if (!hasPassword || selectIsEnclaveSessionValid(getGlobal())) {
      addAccount({ method: 'createAccount' });

      return;
    }
    setIsNewAccountImporting(false);
    setRenderingKey(AccountSelectorState.AddAccountPassword);
  });

  const handleImportAccountClick = useLastCallback(() => {
    if (!hasPassword) {
      addAccount({ method: 'importMnemonic' });

      return;
    }

    setIsNewAccountImporting(true);

    if (selectIsEnclaveSessionValid(getGlobal())) {
      addAccount({ method: 'importMnemonic' });

      return;
    }
    setRenderingKey(AccountSelectorState.AddAccountPassword);
  });

  const handleImportHardwareWalletClick = useLastCallback(() => {
    resetHardwareWalletConnect({
      chain: getChainsSupportingLedger()[0],
      shouldLoadWallets: true,
    });
    setRenderingKey(AccountSelectorState.AddAccountConnectHardware);
  });

  const handleViewModeWalletClick = useLastCallback(() => {
    setShouldReturnToStartScreen(false);
    setRenderingKey(AccountSelectorState.AddAccountViewMode);
  });

  const handleSubmit = useLastCallback((enclaveToken: string) => {
    if (isAddingSubwallet) {
      createSubWallet({ enclaveToken, walletCollectionId: activeCollection?.id });
      handleCloseAccountSelectorForced();
      return;
    }

    addAccount({ method: isNewAccountImporting ? 'importMnemonic' : 'createAccount', enclaveToken });
  });

  const handleHardwareWalletConnected = useLastCallback(() => {
    setRenderingKey(AccountSelectorState.AddAccountSelectHardware);
  });

  const handleViewModeChange = useLastCallback((state: AccountSelectorState) => {
    if (state === AccountSelectorState.Collections) {
      if (renderingKey === AccountSelectorState.Cards || renderingKey === AccountSelectorState.List) {
        setPreviousViewMode(renderingKey);
      }
      setEditingCollectionId(undefined);
    }

    setRenderingKey(state);

    if (state === AccountSelectorState.List || state === AccountSelectorState.Cards) {
      setAccountSelectorViewMode({ mode: state === AccountSelectorState.List ? 'list' : 'cards' });
    }
  });

  const handleSwitchTab = useLastCallback((tabId: number) => {
    if (renderingKey === AccountSelectorState.Reorder) return;

    if (tabId === CREATE_COLLECTION_TAB_ID) {
      setPreviousViewMode(renderingKey);
      setEditingCollectionId(undefined);
      setRenderingKey(AccountSelectorState.CollectionEditor);
      return;
    }

    if (tabId >= COLLECTION_TAB_ID_OFFSET) {
      const collection = networkCollections[tabId - COLLECTION_TAB_ID_OFFSET];
      if (collection) setActiveCollectionId(collection.id);
      return;
    }

    setActiveCollectionId(undefined);
    setAccountSelectorTab({ tab: tabId });
  });

  const handleAddWalletClick = useLastCallback(() => {
    vibrate();
    setPreviousViewMode(renderingKey);
    setPendingWalletCollection({ collectionId: activeCollection?.id });

    if (selectedTab === AccountTab.View) {
      setShouldReturnToStartScreen(true);
      setRenderingKey(AccountSelectorState.AddAccountViewMode);
    } else if (selectedTab === AccountTab.Ledger) {
      setShouldReturnToStartScreen(true);
      handleImportHardwareWalletClick();
    } else {
      setShouldReturnToStartScreen(false);
      setRenderingKey(AccountSelectorState.AddAccountInitial);
    }
  });

  const handleReorderClick = useLastCallback(() => {
    setActiveCollectionId(undefined);
    setRenderingKey(AccountSelectorState.Reorder);
    setAccountSelectorTab({ tab: AccountTab.All });
  });

  const handleReorderDoneClick = useLastCallback(() => {
    vibrate();
    const previousMode = viewModeInitial === 'list'
      ? AccountSelectorState.List
      : AccountSelectorState.Cards;
    setRenderingKey(previousMode);
  });

  const handleCollectionsBack = useLastCallback(() => {
    setRenderingKey(previousViewMode === AccountSelectorState.List
      ? AccountSelectorState.List
      : AccountSelectorState.Cards);
  });

  const handleCollectionEdit = useLastCallback((collectionId?: string) => {
    if (renderingKey === AccountSelectorState.Cards || renderingKey === AccountSelectorState.List) {
      setPreviousViewMode(renderingKey);
    }
    setEditingCollectionId(collectionId);
    setRenderingKey(AccountSelectorState.CollectionEditor);
  });

  const handleCollectionEditorBack = useLastCallback(() => {
    setRenderingKey(AccountSelectorState.Collections);
  });

  useEffect(() => {
    if (!IS_TOUCH_ENV || !isOpen) return;

    const node = contentRef.current;
    if (!node) return;

    function handleSwipe(e: Event, direction: SwipeDirection) {
      if (
        direction === SwipeDirection.Up
        || direction === SwipeDirection.Down
        || (e.target as HTMLElement | null)?.closest(`.${OPEN_CONTEXT_MENU_CLASS_NAME}`)
      ) {
        return false;
      }

      if (direction === SwipeDirection.Right && renderingKey === AccountSelectorState.CollectionEditor) {
        handleCollectionEditorBack();
        return true;
      }

      if (direction === SwipeDirection.Right && renderingKey === AccountSelectorState.Collections) {
        handleCollectionsBack();
        return true;
      }

      if (renderingKey !== AccountSelectorState.Cards && renderingKey !== AccountSelectorState.List) {
        return false;
      }

      const nextIndex = direction === SwipeDirection.Left
        ? Math.min(selectorTabs.length - 2, currentSelectorTabIndex + 1)
        : Math.max(0, currentSelectorTabIndex - 1);
      if (nextIndex === currentSelectorTabIndex) return false;

      const nextTab = selectorTabs[nextIndex];
      handleSwitchTab(nextTab.id);
      return true;
    }

    return captureEvents(node, {
      includedClosestSelector: '.swipe-container',
      selectorToPreventScroll: '.custom-scroll',
      onSwipe: handleSwipe,
    });
  }, [
    currentSelectorTabIndex, handleCollectionEditorBack, handleCollectionsBack, handleSwitchTab,
    isOpen, renderingKey, selectorTabs,
  ]);

  const handleCollectionSave = useLastCallback((collection: Omit<WalletCollection, 'id'> & { id?: string }) => {
    saveWalletCollection(collection);
    setRenderingKey(AccountSelectorState.Collections);
  });

  const handleCollectionDelete = useLastCallback((collectionId: string) => {
    deleteWalletCollection({ id: collectionId });
    if (activeCollectionId === collectionId) setActiveCollectionId(undefined);
    setEditingCollectionId(undefined);
    setRenderingKey(AccountSelectorState.Collections);
  });

  const handleRenameClick = useLastCallback((accountId: string) => {
    vibrate();
    openWalletRenameModal({ accountId });
  });

  const handleLogOutClick = useLastCallback((accountId: string) => {
    vibrate();
    setLogOutAccountId(accountId);
    openLogOutModal();
  });

  const handleLogOutModalClose = useLastCallback(() => {
    closeLogOutModal();
    setLogOutAccountId(undefined);

    if (filteredAccounts.length === 0 || currentAccountId === logOutAccountId) {
      handleCloseAccountSelectorForced();
    }
  });

  const handleOpenSettingWalletVersion = useLastCallback(() => {
    setPendingWalletCollection({});
    handleCloseAccountSelectorForced();
    openSettingsWithState({ state: SettingsState.WalletVersions });
  });

  const handleNewSubwalletClick = useLastCallback(() => {
    if (selectIsEnclaveSessionValid(getGlobal())) {
      const enclaveToken = selectEnclaveToken(getGlobal());
      if (enclaveToken) {
        createSubWallet({ enclaveToken, walletCollectionId: activeCollection?.id });
        handleCloseAccountSelectorForced();
        return;
      }
    }

    if (!hasPassword) {
      addAccount({ method: 'createAccount' });
      return;
    }

    setIsAddingSubwallet(true);
    setRenderingKey(AccountSelectorState.AddAccountPassword);
  });

  function renderHeader(renderingState: AccountSelectorState) {
    const isInactiveTabs = renderingKey === AccountSelectorState.Reorder;

    return (
      <div className={buildClassName(styles.headerWrapper, isScrolled && styles.withBorder)}>
        <AccountSelectorHeader
          walletsCount={filteredAccounts.length}
          totalBalance={totalBalance}
          renderingState={renderingState}
          isSensitiveDataHidden={isSensitiveDataHidden}
          onViewModeChange={handleViewModeChange}
          onReorderClick={handleReorderClick}
        />

        <div className={styles.tabsContainer}>
          <TabList
            tabs={selectorTabs}
            activeTab={currentSelectorTabIndex}
            className={buildClassName(styles.tabs, isInactiveTabs && styles.inactive)}
            onSwitchTab={handleSwitchTab}
          />
        </div>
      </div>
    );
  }

  function renderFooter(renderingState: AccountSelectorState, selectedTab: AccountTab) {
    return (
      <AccountSelectorFooter
        tab={selectedTab}
        renderingState={renderingState}
        withBorder={!noButtonsSeparator}
        onAddWallet={handleAddWalletClick}
        onReorderDone={handleReorderDoneClick}
      />
    );
  }

  function renderContent(isActive: boolean, isFrom: boolean, currentKey: AccountSelectorState) {
    const commonAccountsViewProps = {
      isActive,
      isTestnet,
      filteredAccounts,
      activeTab: selectedTab,
      balancesByAccountId,
      settingsByAccountId,
      addressLineChainsByAccountId,
      currentAccountId,
      isSensitiveDataHidden,
      onScrollInitialize: handleScrollInitialize,
      onScroll: handleScroll,
      onSwitchAccount: handleSwitchAccount,
      onRename: handleRenameClick,
      onReorder: handleReorderClick,
      onLogOut: handleLogOutClick,
    };

    switch (currentKey) {
      case AccountSelectorState.Cards:
        return (
          <>
            {renderHeader(AccountSelectorState.Cards)}
            <AccountsGridView {...commonAccountsViewProps} />
            {renderFooter(AccountSelectorState.Cards, selectedTab)}
          </>
        );

      case AccountSelectorState.List:
        return (
          <>
            {renderHeader(AccountSelectorState.List)}
            <AccountsListView {...commonAccountsViewProps} />
            {renderFooter(AccountSelectorState.List, selectedTab)}
          </>
        );

      case AccountSelectorState.Reorder:
        return (
          <>
            {renderHeader(AccountSelectorState.Reorder)}
            <AccountsListView
              {...commonAccountsViewProps}
              isReorder
              sortState={sortState}
              onDrag={handleDrag}
              onDragEnd={handleDragEnd}
            />
            {renderFooter(AccountSelectorState.Reorder, selectedTab)}
          </>
        );

      case AccountSelectorState.Collections:
        return (
          <WalletCollectionsManager
            isActive={isActive}
            items={collectionManagerItems}
            onEdit={handleCollectionEdit}
            onOrderChange={(orderedIds) => reorderWalletCollectionTabs({ network, orderedIds })}
            onBack={handleCollectionsBack}
            onClose={handleCloseAccountSelectorForced}
          />
        );

      case AccountSelectorState.CollectionEditor: {
        const collection = networkCollections.find(({ id }) => id === editingCollectionId);

        return (
          <WalletCollectionEditor
            key={collection?.id ?? 'new'}
            isActive={isActive}
            collection={collection}
            accounts={orderedAccounts}
            network={network}
            onSave={handleCollectionSave}
            onDelete={handleCollectionDelete}
            onBack={handleCollectionEditorBack}
            onClose={handleCloseAccountSelectorForced}
          />
        );
      }

      case AccountSelectorState.AddAccountInitial:
        return (
          <AddAccountSelector
            isNewAccountImporting={isNewAccountImporting}
            isLoading={isLoading}
            isTestnet={isTestnet}
            hasOtherWalletVersions={hasOtherWalletVersions}
            canAddSubwallet={canAddSubwallet}
            shouldHideBackButton={shouldHideAddAccountBackButton}
            onBack={handleBackFromAddAccount}
            onNewAccountClick={handleNewAccountClick}
            onNewSubwalletClick={handleNewSubwalletClick}
            onImportAccountClick={handleImportAccountClick}
            onImportHardwareWalletClick={handleImportHardwareWalletClick}
            onViewModeWalletClick={handleViewModeWalletClick}
            onOpenSettingWalletVersion={handleOpenSettingWalletVersion}
            onClose={handleCloseAccountSelectorForced}
          />
        );

      case AccountSelectorState.AddAccountPassword:
        return (
          <AddAccountPasswordModal
            isActive={isActive}
            isLoading={isLoading}
            error={error}
            onClearError={clearAccountError}
            onAuthorize={handleSubmit}
            onBack={handleBackFromAddAccount}
            onClose={handleCloseAccountSelectorForced}
          />
        );

      case AccountSelectorState.AddAccountConnectHardware:
        return (
          <div className={modalStyles.transitionContentWrapper}>
            <LedgerConnect
              isActive={isActive}
              onConnected={handleHardwareWalletConnected}
              onBackClick={handleBackFromAddAccount}
              onClose={handleCloseAccountSelectorForced}
            />
          </div>
        );

      case AccountSelectorState.AddAccountSelectHardware:
        return (
          <div className={modalStyles.transitionContentWrapper}>
            <LedgerSelectWallets
              withCloseButton
              onBackClick={handleBackFromAddAccount}
              onClose={handleCloseAccountSelectorForced}
            />
          </div>
        );

      case AccountSelectorState.AddAccountViewMode:
        return (
          <div className={modalStyles.transitionContentWrapper}>
            <AuthImportViewAccount
              isActive={isActive}
              isLoading={isLoading}
              isInModal
              onCancel={handleBackFromAddAccount}
              onClose={handleCloseAccountSelectorForced}
            />
          </div>
        );
    }
  }

  // When opened with `initialAuthState`, Transition sees a key change (old → target) and animates.
  // We pass `name="none"` to make this switch instant, then clear the flag once `renderingKey`
  // catches up to the target so that subsequent navigations animate normally.
  const shouldSkipTransition = skipAnimationToKeyRef.current !== undefined;
  if (skipAnimationToKeyRef.current === renderingKey) {
    skipAnimationToKeyRef.current = undefined;
  }

  return (
    <>
      <Modal
        hasCloseButton
        isOpen={isOpen}
        dialogClassName={styles.modalDialog}
        contentClassName={styles.modalContent}
        onCloseAnimationEnd={handleModalClose}
        onClose={handleCloseAccountSelectorForced}
      >
        <Transition
          ref={contentRef}
          name={shouldSkipTransition ? 'none' : 'semiFade'}
          className={buildClassName(
            modalStyles.transition,
            styles.rootTransition,
            IS_TOUCH_ENV && 'swipe-container',
          )}
          slideClassName={buildClassName(modalStyles.transitionSlide, styles.rootTransitionSlide)}
          activeKey={renderingKey}
        >
          {renderContent}
        </Transition>
      </Modal>

      <LogOutModal isOpen={isLogOutModalOpen} onClose={handleLogOutModalClose} targetAccountId={logOutAccountId} />
    </>
  );
}

export default memo(withGlobal(
  (global): StateProps => {
    const isOpen = global.isAccountSelectorOpen;

    if (!isOpen) {
      return { isOpen: false };
    }

    const {
      accounts,
      accountSelectorActiveTab: activeTab,
      accountSelectorViewMode: viewModeInitial,
      auth: {
        forceAddingTonOnlyAccount,
        initialAddAccountState: initialAuthState,
        shouldHideAddAccountBackButton,
      },
      byAccountId,
      currencyRates,
      settings: {
        byAccountId: settingsByAccountId,
        walletCollections,
        walletCollectionTabOrder,
        baseCurrency,
        isSensitiveDataHidden,
        areTokensWithNoCostHidden,
        isTestnet,
      },
      stakingDefault,
      tokenInfo,
      walletVersions,
    } = global;

    const orderedAccounts = selectOrderedAccounts(global);
    const currentAccountId = selectCurrentAccountId(global)!;
    const networkAccounts = selectNetworkAccounts(global);
    const hasPassword = selectHasPassword(global);
    const canAddSubwallet = selectIsMnemonicAccount(global);
    const accountWalletVersions = walletVersions?.byId?.[currentAccountId];
    const { isLoading, error } = accounts ?? {};

    return {
      isOpen,
      currentAccountId,
      orderedAccounts,
      networkAccounts,
      byAccountId,
      tokenInfo,
      stakingDefault,
      settingsByAccountId,
      walletCollections,
      walletCollectionTabOrder,
      baseCurrency,
      currencyRates,
      areTokensWithNoCostHidden,
      isSensitiveDataHidden,
      activeTab,
      viewModeInitial,
      isTestnet,
      isLoading,
      error,
      hasPassword,
      accountWalletVersions,
      canAddSubwallet,
      forceAddingTonOnlyAccount,
      initialAuthState,
      shouldHideAddAccountBackButton,
    };
  },
  (global, _, stickToFirst) => stickToFirst(selectCurrentAccountId(global)),
)(AccountSelectorModal));
